import React, { memo, useCallback, useEffect, MutableRefObject } from 'react';
import { useFormContext } from 'react-hook-form';
import { Button, Spinner } from '@tcc/crate';
import { ComponentSize, FdsIcon, Icon, IconVariation } from '@tcc/fds';
import { addErrorToast } from 'messages';
import { getMessage } from 'util/locale';
import { useSeedDensityRecommendation } from '../../hooks';
import {
  FormData,
  FieldData,
  SeedDensityRecommendationInput,
  SeedRecommendationOptimizationType,
  RecommendationOptions,
} from '../../types';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RECOMMENDATION_GENERATION_PLANTING_RATE } from 'create/bulk/common/constants';
import { getActiveCropNominalWeight } from 'current/currentSelectors';
import { getAvgFixedPopulationRateMin } from '../../utils';
import { getProductValidationValues } from 'user/selectors';
import { convertTargetYieldToMetric } from '../utils';

interface ActionsCellProps {
  bagSize: number;
  fieldData: FieldData & { _id: string };
  rowIndex: number;
  onRemove: () => void;
  onExpandRow: (force?: boolean) => void;
  update: (index: number, data: FieldData) => void;
  generateHandlersRef: MutableRefObject<Map<string, () => void>>;
}

export const ActionsCell = memo(function Cell({
  bagSize,
  fieldData,
  rowIndex,
  onRemove,
  onExpandRow,
  update,
  generateHandlersRef,
}: ActionsCellProps) {
  const dispatch = useAppDispatch();
  const { trigger, getValues } = useFormContext<FormData>();
  const cropNominalWeight = useAppSelector(getActiveCropNominalWeight);
  const productValidationValues = useAppSelector(getProductValidationValues);
  const { RATE } = productValidationValues?.PLANTING?.CORN || {};

  const { generateRecommendation, isLoading } = useSeedDensityRecommendation({
    fieldId: fieldData._id,
    onSuccess: (data) => {
      const currentFieldData = getValues(`fields.${rowIndex}`);
      update(rowIndex, {
        ...currentFieldData,
        recommendation: data,
        selectedRecommendation: RecommendationOptions.FVSS_RECOMMENDATION,
      });
    },
    onError: () => {
      dispatch(
        addErrorToast(
          getMessage('bulk-fvss-script-generation-recommendation-generation-error', {
            fieldName: fieldData?.name || '',
          })
        )
      );
      onExpandRow(true);
    },
  });

  const handleGenerateRecommendation = useCallback(async () => {
    const isValid = await trigger([
      'grainPrice',
      `fields.${rowIndex}.hybrid`,
      `fields.${rowIndex}.avgFixedPopulation`,
      `fields.${rowIndex}.seedCostPerBag`,
      `fields.${rowIndex}.targetYield`,
    ]);


    if (isValid) {
      const currentFieldData = getValues(`fields.${rowIndex}`);
      const plannedPlantingDate = getValues('plannedPlantingDate');
      const grainPrice = getValues('grainPrice');
      const metricTargetYield = convertTargetYieldToMetric(cropNominalWeight, Number(currentFieldData.targetYield));

      const input: SeedDensityRecommendationInput = {
        recommendationTarget: {
          id: currentFieldData.id,
          type: 'FIELD',
        },
        targetYield: {
          v: metricTargetYield.q,
          u: metricTargetYield.u
        },
        seedProductId: String(currentFieldData.hybrid?.id || ''),
        optimizationType: SeedRecommendationOptimizationType.AGRONOMIC,
        plantingDayOfYear: new Date(plannedPlantingDate).toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit'
        }).replace('/', '-') || '',
        plantingRateInput: {
          min: getAvgFixedPopulationRateMin(RATE),
          max: RECOMMENDATION_GENERATION_PLANTING_RATE.MAX,
          step: RECOMMENDATION_GENERATION_PLANTING_RATE.STEP,
        },
      };

      const economicParameters = {
        grainPrice: Number(grainPrice),
        cropNominalWeight,
        seedsPerBag: bagSize,
        seedCostPerBag: Number(currentFieldData.seedCostPerBag),
      };

      onExpandRow();
      generateRecommendation(input, economicParameters);
    }
  }, [trigger, rowIndex, getValues, RATE, cropNominalWeight, bagSize, onExpandRow, generateRecommendation]);

  // Register/unregister this field's generate handler for "Generate All"
  useEffect(() => {
    const id = fieldData._id;
    const handlers = generateHandlersRef.current;
    if (!fieldData.recommendation && !isLoading) {
      handlers.set(id, handleGenerateRecommendation);
    } else {
      handlers.delete(id);
    }
    return () => { handlers.delete(id); };
  }, [fieldData._id, fieldData.recommendation, isLoading, handleGenerateRecommendation, generateHandlersRef]);

  return (
    <div className="action-cell">
      <Button
        mode="secondary"
        type="button"
        onClick={handleGenerateRecommendation}
        disabled={isLoading}
      >
        {isLoading ? <Spinner size="small" /> : getMessage('button-text__generate')}
      </Button>
      <button type="button" onClick={onRemove} disabled={isLoading}>
        <Icon icon={FdsIcon.Cancel} size={ComponentSize.Lg} variation={IconVariation.Filled} />
      </button>
    </div>
  );
});
